import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number
}

interface UseCountdownOptions {
  onExpire?: () => void
}

const ZERO_TIME: TimeLeft = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  total: 0,
}

export function useCountdown(targetDate: Date | string | null, options?: UseCountdownOptions) {
  const onExpireRef = useRef(options?.onExpire)
  const hasExpiredRef = useRef(false)

  const targetTimestamp = useMemo(() => {
    if (!targetDate) {
      return null
    }

    const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate
    const timestamp = target.getTime()

    return Number.isNaN(timestamp) ? null : timestamp
  }, [targetDate])

  const calculateTimeLeft = useCallback((): TimeLeft => {
    if (!targetTimestamp) {
      return ZERO_TIME
    }

    const difference = targetTimestamp - Date.now()

    if (difference <= 0) {
      return ZERO_TIME
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24))
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)

    return { days, hours, minutes, seconds, total: difference }
  }, [targetTimestamp])

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft())

  useEffect(() => {
    onExpireRef.current = options?.onExpire
  }, [options?.onExpire])

  useEffect(() => {
    hasExpiredRef.current = false

    if (!targetTimestamp) {
      setTimeLeft(ZERO_TIME)
      return
    }

    const updateTimeLeft = () => {
      const nextTimeLeft = calculateTimeLeft()

      setTimeLeft(previous => {
        const didDisplayedValueChange =
          previous.days !== nextTimeLeft.days ||
          previous.hours !== nextTimeLeft.hours ||
          previous.minutes !== nextTimeLeft.minutes ||
          previous.seconds !== nextTimeLeft.seconds ||
          (previous.total <= 0) !== (nextTimeLeft.total <= 0)

        return didDisplayedValueChange ? nextTimeLeft : previous
      })

      if (nextTimeLeft.total <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true
        onExpireRef.current?.()
        return true
      }

      return false
    }

    const isAlreadyExpired = updateTimeLeft()
    if (isAlreadyExpired) {
      return
    }

    const timer = setInterval(() => {
      const hasExpired = updateTimeLeft()
      if (hasExpired) {
        clearInterval(timer)
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [targetTimestamp, calculateTimeLeft])

  const isExpired = timeLeft.total <= 0

  return { timeLeft, isExpired }
}

// Format number with leading zero
export function padNumber(num: number): string {
  return num.toString().padStart(2, '0')
}
